import { FixedEventDef } from '../types';

/**
 * 固定剧情时间线表（全局主线事件；2026-10-17 建，源文档 docs/主线剧情/固定剧情时间线.md）
 *
 * ★ 契约：写进本数组的每一条，只要带 `trigger.calendar`（哪年～哪年哪月，展示年 1-indexed）
 *   或 `trigger.stage`（咖位 ≥ N），就会自动扫描、到点入队演出——不需要任何别的接线。写入即实装。
 *   - 日历事件在**月之初**（`checkCalendarAtMonthStart`）扫，**写的月份＝上屏的月份**；stage 在月底扫、次月演。
 *   - 一次性：以事件 id 作 flag，交付（`PlotSystem.deliver`）即点亮，之后不再重复；
 *   - 载体三选一：encounter（VN）/ message（月底人脉页私信）/ media（弹窗：weibo / news / hotpost）；
 *   - **宿主默认＝决策页**（2026-10-17 茄子令：固定剧情在「颁奖结果新闻卡之后、月度执行页之前」的决策页触发）；
 *     某条想放别处（如执行档）就在该条自己写 `playOn`，以它为准；
 *   - 带 `trigger.location` 的**不**走日历扫描（那是「到访才触发」的地点事件，另走地图派发口）。
 *
 * ⛔ 别把「依赖剧本钩子 / NPC 初遇」的剧情写这里——那类走 `FixedEvents.ts` + SpecialScripts.plotHooks。
 *   本表只放「不依赖玩家选择、到某个时点必发生」的全局主线事件。
 */
const TIMELINE_EVENTS_RAW: FixedEventDef[] = [
    {
        // 【正式文案 2026-10-17】乔子珊女团解散：Y2 上半年铺陈「选秀女团人气王」背景
        // （源文档 docs/主线剧情/固定剧情时间线.md 事件 1；纯新闻播报，不结算名气/好感/剧热度）
        // 触发＝全局日历一次性，Y2 年 3 月（2026-10-30 由 Y2M4 挪入）；微博营销号详情页
        // 配图：image 只写「media/文件名」，图片本身放 web/public/media/（文件名与文档同步；
        //   文件缺失＝不显示图、不报错；换图＝改名 + 改这里，见 docs/特殊剧情/填写格式.md §配图）
        id: 'FIXED_QZS_FAREWELL',
        medium: 'media',
        trigger: { calendar: { from: { year: 2, month: 3 }, to: { year: 2, month: 3 } } },
        media: {
            kind: 'weibo',
            author: '娱圈八爷',
            body: '【#乔子珊#告别典礼泪洒全场】从节目《星光创造营》出道的女团NOVAE成团三年之期届满，于昨日正式解散。告别典礼尾声，人气成员乔子珊数度哽咽，一句"以后就没有NOVAE了"让全场泪崩。#NOVAE解散#',
            image: 'media/qzs-farewell.webp',   // ← 配图：文件放 web/public/media/qzs-farewell.webp
            comments: [
                '从出道看到解散，这三年好像很短又很长',
                '乔子珊真的撑起了半个团，泪目',
                '限定团就是倒计时，从第一天就在告别',
            ],
            buzz: 350,      // 话题度（讨论/转发/点赞量级锚，源文档定 350）
        },
    },
    {
        // 【正式文案 2026-10-17 茄子】影后下凡传闻：庄文茵被爆主演《承天》
        // （源文档 docs/主线剧情/固定剧情时间线.md 事件 2；纯新闻播报，不结算名气/好感/剧热度；news 图文卡 card 版式）
        // 触发＝全局日历 Y1 年 9 月（展示年 1）；宿主默认决策页（本表统一补）
        id: 'FIXED_ZWY_CHENGTIAN',
        medium: 'media',
        trigger: { calendar: { from: { year: 1, month: 9 }, to: { year: 1, month: 9 } } },
        media: {
            kind: 'news',
            buzz: 200,      // 话题度（讨论/转发量级锚，源文档定 200）
            title: '四十四岁庄文茵被爆将主演《承天》 首触小荧屏出演辽太后萧燕燕',
            body: '近日，网传曾二度获封金枢影后的庄文茵将主演历史传奇大剧《承天》，饰演辽太后萧燕燕。该剧投资巨大、制作班底顶级，被视为年度大饼。消息传出后引发热议，不少观众期待影后首度主演电视剧。目前片方尚未正式官宣。',
            comments: [],
        },
    },
];

/**
 * 固定剧情时间线表。默认宿主＝**决策页**（2026-10-17 茄子令：固定剧情在「颁奖结果新闻卡之后、
 * 月度执行页之前」的决策页触发；链＝`DecisionPanel.onConfirm` → 颁奖夜 → `playDecisionPlotThen`
 * → 开月度执行页）。某条想放别处（如执行档）就在该条自己写 `playOn`，以它为准。
 */
export const TIMELINE_EVENTS: FixedEventDef[] = TIMELINE_EVENTS_RAW.map(ev => ({
    ...ev,
    playOn: ev.playOn ?? 'decision',
}));
